export type InputSource = 'keyboard' | 'controller' | 'touch';
export type GameAction =
  | 'confirm'
  | 'attack'
  | 'dash'
  | 'pause'
  | 'mute'
  | 'volume-up'
  | 'volume-down';
export type MovementDirection = 'up' | 'down' | 'left' | 'right';

export interface MovementVector {
  readonly x: number;
  readonly y: number;
}

export interface InputFrame {
  readonly movement: MovementVector;
  readonly actions: readonly GameAction[];
  readonly activeSources: readonly InputSource[];
}

const SOURCE_ORDER: readonly InputSource[] = ['keyboard', 'controller', 'touch'];

const normalizeMovement = ({ x, y }: MovementVector): MovementVector => {
  const magnitude = Math.hypot(x, y);
  if (magnitude === 0) return { x: 0, y: 0 };
  if (magnitude <= 1) return { x, y };
  if (Math.abs(x) === Math.abs(y)) {
    return { x: Math.sign(x) * Math.SQRT1_2, y: Math.sign(y) * Math.SQRT1_2 };
  }
  return { x: x / magnitude, y: y / magnitude };
};

export class InputBuffer {
  readonly #movements = new Map<InputSource, MovementVector>();
  readonly #actions: { readonly source: InputSource; readonly action: GameAction }[] = [];
  readonly #actionSources = new Set<InputSource>();

  public setMovement(source: InputSource, movement: MovementVector): void {
    const normalized = normalizeMovement(movement);
    if (normalized.x === 0 && normalized.y === 0) {
      this.#movements.delete(source);
      return;
    }
    this.#movements.set(source, normalized);
  }

  public queueAction(source: InputSource, action: GameAction): void {
    if (!this.#actions.some((queued) => queued.source === source && queued.action === action)) {
      this.#actions.push({ source, action });
    }
    this.#actionSources.add(source);
  }

  public clearSource(source: InputSource): void {
    this.#movements.delete(source);
    this.#actionSources.delete(source);
    for (let index = this.#actions.length - 1; index >= 0; index -= 1) {
      if (this.#actions[index]?.source === source) this.#actions.splice(index, 1);
    }
  }

  public readFrame(): InputFrame {
    let x = 0;
    let y = 0;
    for (const movement of this.#movements.values()) {
      x += movement.x;
      y += movement.y;
    }

    const activeSources = SOURCE_ORDER.filter(
      (source) => this.#movements.has(source) || this.#actionSources.has(source),
    );
    const frame: InputFrame = {
      movement: normalizeMovement({ x, y }),
      actions: [...new Set(this.#actions.map(({ action }) => action))],
      activeSources,
    };
    this.#actions.length = 0;
    this.#actionSources.clear();
    return frame;
  }
}

export interface KeyboardBindings {
  readonly moveUp: readonly string[];
  readonly moveDown: readonly string[];
  readonly moveLeft: readonly string[];
  readonly moveRight: readonly string[];
  readonly confirm: readonly string[];
  readonly attack: readonly string[];
  readonly dash: readonly string[];
  readonly pause: readonly string[];
  readonly mute: readonly string[];
  readonly volumeUp: readonly string[];
  readonly volumeDown: readonly string[];
}

export const DEFAULT_KEYBOARD_BINDINGS: KeyboardBindings = {
  moveUp: ['KeyW', 'ArrowUp'],
  moveDown: ['KeyS', 'ArrowDown'],
  moveLeft: ['KeyA', 'ArrowLeft'],
  moveRight: ['KeyD', 'ArrowRight'],
  confirm: ['Space', 'Enter'],
  attack: ['KeyZ'],
  dash: ['KeyX'],
  pause: ['Escape'],
  mute: ['KeyM'],
  volumeUp: ['Equal', 'NumpadAdd', 'BracketRight'],
  volumeDown: ['Minus', 'NumpadSubtract', 'BracketLeft'],
};

const ACTION_BINDINGS: readonly [keyof KeyboardBindings, GameAction][] = [
  ['confirm', 'confirm'],
  ['attack', 'attack'],
  ['dash', 'dash'],
  ['pause', 'pause'],
  ['mute', 'mute'],
  ['volumeUp', 'volume-up'],
  ['volumeDown', 'volume-down'],
];

export class KeyboardInputAdapter {
  readonly #heldCodes = new Set<string>();

  public constructor(
    private readonly input: InputBuffer,
    private bindings: KeyboardBindings = DEFAULT_KEYBOARD_BINDINGS,
  ) {}

  public setBindings(bindings: KeyboardBindings): void {
    this.releaseAll();
    this.bindings = bindings;
  }

  public keyDown(code: string, repeat = false): boolean {
    const binding = this.#findBinding(code);
    if (binding === undefined) return false;

    this.#heldCodes.add(code);
    this.#updateMovement();
    if (!repeat) {
      const action = ACTION_BINDINGS.find(([name]) => name === binding)?.[1];
      if (action !== undefined) this.input.queueAction('keyboard', action);
    }
    return true;
  }

  public keyUp(code: string): boolean {
    const recognized = this.#findBinding(code) !== undefined;
    this.#heldCodes.delete(code);
    this.#updateMovement();
    return recognized;
  }

  public releaseAll(): void {
    this.#heldCodes.clear();
    this.input.clearSource('keyboard');
  }

  #findBinding(code: string): keyof KeyboardBindings | undefined {
    return (Object.keys(this.bindings) as (keyof KeyboardBindings)[]).find((name) =>
      this.bindings[name].includes(code),
    );
  }

  #isHeld(binding: keyof KeyboardBindings): boolean {
    return this.bindings[binding].some((code) => this.#heldCodes.has(code));
  }

  #updateMovement(): void {
    this.input.setMovement('keyboard', {
      x: Number(this.#isHeld('moveRight')) - Number(this.#isHeld('moveLeft')),
      y: Number(this.#isHeld('moveDown')) - Number(this.#isHeld('moveUp')),
    });
  }
}

export interface GamepadButtonSnapshot {
  readonly pressed: boolean;
  readonly value: number;
}

export interface GamepadSnapshot {
  readonly axes: readonly number[];
  readonly buttons: readonly GamepadButtonSnapshot[];
}

const CONTROLLER_ACTIONS: readonly [number, GameAction][] = [
  [0, 'attack'],
  [1, 'dash'],
  [3, 'confirm'],
  [9, 'pause'],
  [8, 'mute'],
  [4, 'volume-down'],
  [5, 'volume-up'],
];
const CONTROLLER_DIRECTION_BUTTONS = [12, 13, 14, 15] as const;

export class GamepadInputAdapter {
  readonly #pressedActions = new Set<GameAction>();
  #waitingForNeutral = false;

  public constructor(
    private readonly input: InputBuffer,
    private readonly deadzone = 0.25,
  ) {}

  public poll(gamepad: GamepadSnapshot): void {
    if (this.#waitingForNeutral) {
      const hasActiveAxis = gamepad.axes
        .slice(0, 2)
        .some((axis) => Math.abs(axis) >= this.deadzone);
      const hasPressedButton = [
        ...CONTROLLER_ACTIONS.map(([index]) => index),
        ...CONTROLLER_DIRECTION_BUTTONS,
      ].some((index) => gamepad.buttons[index]?.pressed ?? false);
      if (hasActiveAxis || hasPressedButton) {
        this.input.clearSource('controller');
        return;
      }
      this.#waitingForNeutral = false;
    }

    const axisX = Math.abs(gamepad.axes[0] ?? 0) >= this.deadzone ? (gamepad.axes[0] ?? 0) : 0;
    const axisY = Math.abs(gamepad.axes[1] ?? 0) >= this.deadzone ? (gamepad.axes[1] ?? 0) : 0;
    const dpadX = Number(gamepad.buttons[15]?.pressed ?? false) - Number(gamepad.buttons[14]?.pressed ?? false);
    const dpadY = Number(gamepad.buttons[13]?.pressed ?? false) - Number(gamepad.buttons[12]?.pressed ?? false);
    this.input.setMovement('controller', {
      x: dpadX === 0 ? axisX : dpadX,
      y: dpadY === 0 ? axisY : dpadY,
    });

    const nextPressed = new Set<GameAction>();
    for (const [index, action] of CONTROLLER_ACTIONS) {
      if (!(gamepad.buttons[index]?.pressed ?? false)) continue;
      nextPressed.add(action);
      if (!this.#pressedActions.has(action)) this.input.queueAction('controller', action);
    }
    this.#pressedActions.clear();
    for (const action of nextPressed) this.#pressedActions.add(action);
  }

  public suspendUntilNeutral(): void {
    this.#pressedActions.clear();
    this.input.clearSource('controller');
    this.#waitingForNeutral = true;
  }

  public disconnect(): void {
    this.#pressedActions.clear();
    this.input.clearSource('controller');
    this.#waitingForNeutral = false;
  }
}

export class TouchInputAdapter {
  readonly #directions = new Set<MovementDirection>();

  public constructor(private readonly input: InputBuffer) {}

  public setDirection(direction: MovementDirection, active: boolean): void {
    if (active) this.#directions.add(direction);
    else this.#directions.delete(direction);
    this.#updateMovement();
  }

  public activate(action: GameAction): void {
    this.input.queueAction('touch', action);
  }

  public releaseAll(): void {
    this.#directions.clear();
    this.input.clearSource('touch');
  }

  #updateMovement(): void {
    this.input.setMovement('touch', {
      x: Number(this.#directions.has('right')) - Number(this.#directions.has('left')),
      y: Number(this.#directions.has('down')) - Number(this.#directions.has('up')),
    });
  }
}
